import React, { useState, useCallback, useEffect } from 'react';
import { Toast } from './Toast';

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning' | 'confirm';
  details?: string[];
  onConfirm?: () => void;
  onCancel?: () => void;
}

// Crear un evento personalizado para los toasts
const TOAST_EVENT = 'show-toast';
interface ToastEvent extends CustomEvent {
  detail: {
    message: string;
    type: ToastMessage['type'];
    details?: string[];
    onConfirm?: () => void;
    onCancel?: () => void;
  };
}

// Función global para mostrar toasts
export const showToast = (
  message: string, 
  type: ToastMessage['type'] = 'info',
  details?: string[],
  onConfirm?: () => void,
  onCancel?: () => void
) => {
  const event = new CustomEvent(TOAST_EVENT, {
    detail: { message, type, details, onConfirm, onCancel }
  });
  window.dispatchEvent(event);
};

// Función de ayuda para mostrar confirmaciones
export const showConfirmToast = (
  message: string,
  details: string[],
  onConfirm: () => void,
  onCancel?: () => void
) => {
  showToast(message, 'confirm', details, onConfirm, onCancel);
};

export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [counter, setCounter] = useState(0);

  const addToast = useCallback((
    message: string, 
    type: ToastMessage['type'] = 'info',
    details?: string[],
    onConfirm?: () => void,
    onCancel?: () => void
  ) => {
    const id = counter;
    setCounter(prev => prev + 1);
    setToasts(prev => [...prev, { id, message, type, details, onConfirm, onCancel }]);
  }, [counter]);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Escuchar eventos de toast
  useEffect(() => {
    const handleToast = (event: Event) => {
      const { message, type, details, onConfirm, onCancel } = (event as ToastEvent).detail;
      addToast(message, type, details, onConfirm, onCancel);
    };

    window.addEventListener(TOAST_EVENT, handleToast);
    return () => window.removeEventListener(TOAST_EVENT, handleToast);
  }, [addToast]);

  return (
    <>
      {toasts.map((toast, index) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
          position={{ top: 20 + (index * 70), right: 20 }}
          details={toast.details}
          onConfirm={toast.onConfirm}
          onCancel={toast.onCancel}
        />
      ))}
    </>
  );
};

// Hook para usar el toast desde cualquier componente
export const useToast = () => {
  const [toastContainer] = useState(() => {
    const container = document.createElement('div');
    container.id = 'toast-container';
    document.body.appendChild(container);
    return container;
  });

  const showToast = useCallback((message: string, type: ToastMessage['type'] = 'info') => {
    // Aquí implementaremos la lógica para mostrar el toast
    const toast = document.createElement('div');
    toast.className = `toast ${type}`;
    toast.textContent = message;
    toastContainer.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('hide');
      setTimeout(() => {
        toastContainer.removeChild(toast);
      }, 300);
    }, 5000);
  }, [toastContainer]);

  return { showToast };
}; 